import type {
  TWorkflowDraftSavePayload,
  TWorkflowElement,
  TWorkflowElementKind,
  TWorkflowElementWrite,
} from "@/types/form-workflow";

/**
 * Pure helpers for the Form Workflow autosave (Epic #2086, persistence #2116):
 * generate the client-stable element ids the PUT round-trips, and serialize the
 * in-memory ``elements[]`` into the whole-document save body.
 *
 * Client-supplied ids are load-bearing: any element referenced as a
 * ``parent_id`` / ``end.reopen_dest_id``, and any decision option (whose id IS a
 * child lane's ``branch_key``), must carry a UUID the server adopts as the row
 * PK so references survive across saves. Use {@link newWorkflowId} at insert time.
 */

/** A client-generated UUID for a new element or decision option. */
export function newWorkflowId(): string {
  return crypto.randomUUID();
}

/** The subtype key for an element kind. */
const SUBTYPE_KEY: Record<
  TWorkflowElementKind,
  "page" | "review" | "branch" | "change_owner" | "end"
> = {
  page: "page",
  review: "review",
  branch: "branch",
  change_owner: "change_owner",
  end: "end",
};

/**
 * Serialize ``elements[]`` into the PUT body. Each element emits its common
 * fields + ONLY the subtype object matching its ``kind`` (never the other
 * three), preserving server/client ids verbatim. Decision options carry their
 * ids so a Review's branch lanes (keyed by option id) round-trip.
 *
 * [#2154] the ``page`` subtype's content field (``fields`` today → the intake
 * element tree later) serializes exactly as the intake builder serializes
 * ``page.elements``; when Dan delivers the schema, this passthrough swaps to
 * reuse that serialization.
 */
export function toSavePayload(
  name: string,
  elements: TWorkflowElement[],
): TWorkflowDraftSavePayload {
  // Defense-in-depth: the store guards against an empty save (the BE rejects
  // ``elements: []`` because the first Page is fixed), but a programming error
  // upstream shouldn't silently wipe the draft. Log loudly in dev rather than
  // throw — the caller's guard is the real backstop.
  if (import.meta.env.DEV && elements.length === 0) {
    console.error("form-workflow: toSavePayload called with no elements", { name });
  }

  const writes: TWorkflowElementWrite[] = elements.map((el) => {
    const write: TWorkflowElementWrite = {
      id: el.id,
      kind: el.kind,
      name: el.name,
      position: el.position,
      parent_id: el.parent_id,
      branch_key: el.branch_key,
    };
    const key = SUBTYPE_KEY[el.kind];
    const subtype = el[key];
    if (subtype) {
      // Only the matching subtype object is attached; the other three stay
      // absent so the BE's "kind ⇔ subtype" validation passes.
      (write as unknown as Record<string, unknown>)[key] = subtype;
    }
    return write;
  });

  return { name, elements: writes };
}
